import { differenceInCalendarDays, parseISO, isAfter } from 'date-fns';
import { logger } from './logger';
import { todayIST } from './locale';

/**
 * Rule-based "next best action" advisor.
 * Returns a short, actionable suggestion for a given task row, or `null` if
 * there's nothing meaningful to say (avoid hint-spam).
 *
 * Priorities, top down:
 *   1. Delegated + past expected return → "Nudge <who>"
 *   2. URGENT priority + no scheduled date → "Slot it now"
 *   3. Overdue (scheduledDate < today, not completed) → "Reschedule or delegate"
 *   4. DEEP_WORK type + short estimate (<25m) → "Batch with similar tasks"
 *   5. Otherwise → null
 */
export function getNextBestAction(task, todayStr) {
  if (!task || task.status === 'COMPLETED' || task.status === 'ARCHIVED') return null;
  const today = todayStr || todayIST(); // tz-correct fallback (DATETIME-IST-TODAY-SWEEP-1)
  // 1. Delegated overdue
  if (task.delegatedTo && task.expectedReturnDate) {
    try {
      const expected = parseISO(task.expectedReturnDate);
      if (isAfter(parseISO(today), expected)) {
        const who = task.delegatedTo;
        const days = differenceInCalendarDays(parseISO(today), expected);
        return { tone: 'danger', text: `Overdue ${days}d from ${who} — nudge` };
      }
    } catch (err) {
      logger.warn('nextBestAction: invalid expectedReturnDate', task.expectedReturnDate, err?.message);
    }
  }
  // 2. URGENT without schedule
  if ((task.priority === 'URGENT' || task.priority === 'HIGH') && !task.scheduledDate && !task.dueDate) {
    return { tone: 'warning', text: 'Slot it into Today' };
  }
  // 3. Overdue scheduled
  if (task.scheduledDate && task.scheduledDate < today && task.status !== 'TODAY') {
    try {
      const days = differenceInCalendarDays(parseISO(today), parseISO(task.scheduledDate));
      if (days > 0) return { tone: 'warning', text: `${days}d overdue — reschedule or drop` };
    } catch (err) {
      logger.warn('nextBestAction: invalid scheduledDate', task.scheduledDate, err?.message);
    }
  }
  // 4. Deep work + small estimate
  if (task.taskType === 'DEEP_WORK' && (task.estimatedMinutes || 0) <= 25) {
    return { tone: 'info', text: 'Batch with similar deep-work blocks' };
  }
  return null;
}

export function toneToColor(tone) {
  switch (tone) {
    case 'danger':  return 'var(--pd-crimson)';
    case 'warning': return 'var(--pd-warning)';
    case 'info':    return 'var(--pd-focus)';
    default:        return 'var(--pd-subtle)';
  }
}
